import { DOCUMENT, isPlatformBrowser } from "@angular/common";
import { Inject, Injectable, PLATFORM_ID } from "@angular/core";
import { environment } from "@env/environment";

/**
 * TODO - Create a parent class for storage service for generalizing Local and 
 *        Cookie storage classes
 */
@Injectable()
export class CookieService {

    static readonly APP_AUTH_ACCESS_TOKEN_KEY = `LAUNCH-AUTH-${environment.storageKeys.auth.accessToken !== "" ? environment.storageKeys.auth.accessToken : 'accessToken'}`;
    static readonly APP_AUTH_REFRESH_TOKEN_KEY = `LAUNCH-AUTH-${environment.storageKeys.auth.refreshToken !== "" ? environment.storageKeys.auth.refreshToken : 'refreshToken'}`;
    static readonly APP_AUTH_REFRESH_TOKEN_EXPIRY_KEY = `LAUNCH-AUTH-${environment.storageKeys.auth.refreshTokenExpiry !== "" ? environment.storageKeys.auth.refreshTokenExpiry : 'refreshTokenExpiry'}`;

    private readonly documentIsAccessible: boolean;
  
    constructor(
      @Inject(DOCUMENT) private document: Document,
      // Get the `PLATFORM_ID` so we can check if we're in a browser.
      @Inject(PLATFORM_ID) private platformId: Object
    ) {
      this.documentIsAccessible = isPlatformBrowser(this.platformId);
    }

    /**
     * Gets the unencoded version of an encoded component of a Uniform Resource Identifier (URI).
     *
     * @param encodedURIComponent A value representing an encoded URI component.
     *
     * @returns The unencoded version of an encoded component of a Uniform Resource Identifier (URI).
     */
    private static safeDecodeURIComponent(encodedURIComponent: string): string {
        try {
            return decodeURIComponent(encodedURIComponent);
        } catch {
            // probably it is not uri encoded. return as is
            return encodedURIComponent;
        }
    }

    /**
     * Get cookie Regular Expression
     *
     * @param name Cookie name
     * @returns property RegExp
     *
     */
    private static getCookieRegExp(name: string): RegExp {
        const escapedName: string = name.replace(/([\[\]{}()|=;+?,.*^$])/gi, '\\$1');

        return new RegExp('(?:^' + escapedName + '|;\\s*' + escapedName + ')=(.*?)(?:;|$)', 'g');
    }


    /**
     * Return `true` if {@link Document} is accessible, otherwise return `false`
     * @param name Cookie name
     * @returns boolean - whether cookie with specified name exists
     */
    check(name: string): boolean {
        if (!this.documentIsAccessible) {
            return false;
        }
        name = encodeURIComponent(name);
        const regExp: RegExp = CookieService.getCookieRegExp(name);
        return regExp.test(this.document.cookie);
    }


    /**
     * Get cookies by name
     *
     * @param name Cookie name
     * @returns property value
     * 
     */
    get(name: string): unknown {
        if (this.documentIsAccessible && this.check(name)) {
            name = encodeURIComponent(name);

            const regExp: RegExp = CookieService.getCookieRegExp(name);
            const result: RegExpExecArray | null = regExp.exec(this.document.cookie);

            if(result) {
                return CookieService.safeDecodeURIComponent(result[1])
            }
        }
        return null
    }

    /**
     * Set cookies by name, value, expires
     * 
     * @param name 
     * @param value 
     * @param expires 
     * @returns 
     */
    set(name: string, value: string, expires?: number): void {
        if (!this.documentIsAccessible) {
          return;
        }
    
        let cookieString: string = encodeURIComponent(name) + '=' + encodeURIComponent(value) + ';';
    
        // // default for Fri Dec 31 9999 23:59:59 GMT-0500 (Eastern Standard Time) - implies forever token
        // let dateExpires: Date = new Date( 253402318799 * 1000 )
        
        if (expires) {
            const dateExpires = new Date( expires * 1000 );
            cookieString += 'expires=' + dateExpires.toUTCString() + ';';
        }

        cookieString += 'path=/;';
    
        this.document.cookie = cookieString;
    }

    /**
     * Delete cookie by name
     *
     * @param name   Cookie name
     */
    delete(name: string): void {
        if (!this.documentIsAccessible) {
            return;
        }
        this.set(name, '', 1);
    }
}